import React, { useEffect, useState } from 'react';
import moment from 'moment';
import css from './accuracy-overview-graph.scss';

import ContextModal from '../context-modal/context-modal';
import { CONTEXT_MODAL_POSITION } from '../context-modal/context-modal-constants';

import {
    GRAPH_HEIGHT,
    MONTH_NAMES,
    DAYS,
    WEEKS,
    MONTHS
} from './accuracy-overview.constants';

// generates the horizontal lines with percentage values
export const renderPercentageLines = () => {
    const PercentageLines = [];

    for (let i = 0; i <= 100; i += 10) {
        PercentageLines.unshift(
            <div className={css.percentageLine} key={i}>
                <div className={css.percentage}>{i}%</div>
                <div className={css.line} />
            </div>
        );
    }

    return PercentageLines;
};

// function to return days when dates selected have range of 14 days or less
export const getDays = (fromDate = '', toDate = '', fromYear, toYear) => {
    const daysToDisplay = [];

    if (fromDate === '' || toDate === '') return daysToDisplay;
    fromDate = moment(fromDate);
    toDate = moment(toDate);
    for (let date = fromDate; date <= toDate; ) {
        if (date.date() === 1) {
            daysToDisplay.push(
                `${date.date()} ${MONTH_NAMES[date.month() % 12]}`
            );
        } else if (date.isSame(fromDate)) {
            daysToDisplay.push(
                `${fromDate.date()} ${
                    MONTH_NAMES[date.month() % 12]
                } ${fromYear}`
            );
        } else if (date.isSame(toDate)) {
            daysToDisplay.push(
                `${date.date()} ${MONTH_NAMES[date.month() % 12]} ${toYear}`
            );
        } else {
            daysToDisplay.push(`${date.date()}`);
        }
        date = moment(date).add(1, DAYS);
    }
    return daysToDisplay;
};

export const getWeeks = (fromDate = '', toDate = '') => {
    const weeksToDisplay = [];

    if (fromDate === '' || toDate === '') return weeksToDisplay;

    let currentDate;

    for (let date = fromDate; date <= toDate; ) {
        if (
            date.format('D MMM YYYY') === fromDate.format('D MMM YYYY') ||
            date.format('D MMM YYYY') === toDate.format('D MMM YYYY')
        ) {
            weeksToDisplay.push(date.format('D MMM YYYY'));
        } else {
            weeksToDisplay.push(date.format('D MMM'));
        }
        currentDate = date;
        date = moment(date).add(7, DAYS);
    }

    // format last date on x-axis to have year
    weeksToDisplay[weeksToDisplay.length - 1] = moment(currentDate).format(
        'D MMM YYYY'
    );

    return weeksToDisplay;
};

// function to return months when dates selected have range 3 months or more
export const getMonths = (fromDate, start, end, fromYear, diffYear) => {
    const monthsToDisplay = [];

    if (!fromDate) {
        return monthsToDisplay;
    }

    start = moment(start);
    end = moment(end);

    for (let date = fromDate.month(); date <= diffYear; date++) {
        if (date === fromDate.month()) {
            monthsToDisplay.push(start.format('D MMM YYYY'));
        } else if (date === diffYear) {
            monthsToDisplay.push(end.format('D MMM YYYY'));
        } else {
            monthsToDisplay.push(MONTH_NAMES[date % 12]);
        }
    }

    return monthsToDisplay;
};

// based on the date range selected, returns days or months
export const getDateRange = (dates = '') => {
    let datesToDisplay = [];
    let unitToDisplay = '';

    if (!dates) {
        return datesToDisplay;
    }
    const [start, end] = dates.split('-');

    const fromDate = moment(start);
    const toDate = moment(end);

    const fromYear = fromDate.year();
    const toYear = toDate.year();

    const daysDifference = toDate.diff(fromDate, 'days');

    if (daysDifference <= 0) {
        return [datesToDisplay, unitToDisplay];
    } else if (daysDifference <= 90) {
        datesToDisplay = getWeeks(fromDate, toDate);
        unitToDisplay = WEEKS;
    } else {
        const diffYear = 12 * (toYear - fromYear) + toDate.month();
        datesToDisplay = getMonths(fromDate, start, end, fromYear, diffYear);
        unitToDisplay = MONTHS;
    }

    return [datesToDisplay, unitToDisplay];
};

// generates the data on the x-axis (months, weeks or days)
export const renderDateRange = (dates = '') => {
    if (dates === '') return null;

    const [widthPerItem, setWidthPerItem] = useState(0);

    const Months = [];

    let width;
    const [dateRange] = getDateRange(dates);

    useEffect(() => {
        width =
            window.document.getElementById(MONTHS) &&
            window.document.getElementById(MONTHS).offsetWidth;

        setWidthPerItem(width / dateRange.length);
    }, [window.document.getElementById('month'), widthPerItem]);

    dateRange.forEach((date) => {
        Months.push(
            <div key={date} style={{ width: `${widthPerItem}px` }}>
                {date}
            </div>
        );
    });

    return Months;
};

export const generateChartDataForWeeks = (dates = '', averageAccuracies) => {
    const chartData = [];

    if (dates === '' || !averageAccuracies) return chartData;

    let [startDate, endDate] = dates.split('-');

    startDate = moment(startDate);
    endDate = moment(endDate);

    for (let date = startDate; date <= endDate; ) {
        chartData.push({
            date: date.format('D MMM YYYY'),
            accuracy: null
        });

        date = moment(date).add(7, DAYS);
    }

    chartData.forEach((data) => {
        averageAccuracies.forEach(({ weekStartDate, accuracy }) => {
            if (moment(weekStartDate).isSame(moment(data.date), 'week')) {
                data.accuracy = accuracy;
            }
        });
    });

    return chartData;
};

/*
    renders the point on the graph
    point should be plotted based on accuracy and month/week/day combination
*/
export const renderChartPoints = (dates, averageAccuracies) => {
    // calculate the width of each column based on the date range
    let width;

    const [widthPerItem, setWidthPerItem] = useState(0);
    const ChartPoints = [];

    const chartData = generateChartDataForWeeks(dates, averageAccuracies);

    useEffect(() => {
        width =
            window.document.getElementById(MONTHS) &&
            window.document.getElementById(MONTHS).offsetWidth;

        setWidthPerItem(width / chartData.length);
    }, [window.document.getElementById('month'), widthPerItem]);

    // for each date range calculate the position when chart point will be placed
    chartData.forEach((data, index) => {
        const [isMouseOverChartPoint, setMouseOverChartPoint] = useState(false);

        const currentAveragePointPosition =
            data.accuracy === null
                ? null
                : GRAPH_HEIGHT - (GRAPH_HEIGHT / 100) * data.accuracy;

        let nextAveragePointPosition = null;
        let indexDifference;

        for (let i = index + 1; i < chartData.length; i++) {
            nextAveragePointPosition =
                chartData[i].accuracy === null
                    ? null
                    : GRAPH_HEIGHT -
                      (GRAPH_HEIGHT / 100) * chartData[i].accuracy;

            if (nextAveragePointPosition !== null) {
                indexDifference = i - index;
                break;
            }
        }

        ChartPoints.push(
            <div
                key={data.date}
                className={css.chartLine}
                style={{
                    width: `${widthPerItem}px`
                }}
            >
                {data.accuracy !== null && (
                    <span className={css.chartPointContainer}>
                        <div
                            className={css.chartPoint}
                            style={{
                                top: currentAveragePointPosition
                            }}
                            onMouseOver={() => setMouseOverChartPoint(true)}
                            onMouseOut={() => setMouseOverChartPoint(false)}
                        >
                            <ContextModal
                                show={isMouseOverChartPoint}
                                className={css.contextModal}
                                onClickOutsideHandler={() => {}}
                                position={CONTEXT_MODAL_POSITION.MODAL_DOWN}
                            >
                                <span>
                                    <div>{data.date}</div>
                                    <div className={css.accuracy}>
                                        {data.accuracy}%
                                    </div>
                                </span>
                            </ContextModal>
                        </div>
                        <svg
                            height={GRAPH_HEIGHT}
                            width={widthPerItem}
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <polyline
                                points={`${widthPerItem /
                                    2}, ${currentAveragePointPosition}, ${indexDifference *
                                    widthPerItem +
                                    widthPerItem /
                                        2}, ${nextAveragePointPosition}`}
                                style={{
                                    fill: 'none',
                                    stroke: '#1981c4',
                                    strokeWidth: 2
                                }}
                            />
                        </svg>
                    </span>
                )}
            </div>
        );
    });

    return ChartPoints;
};



// WEBPACK FOOTER //
// ./components/accuracy-overview-graph/accuracy-overview-graph.utils.js